import React from 'react'
import { Chip, makeStyles } from '@material-ui/core'
import type { ChipProps } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../'
import { Tooltip, useMediaQuery } from '../../'
import { NewAltIcon } from '../../icons'
import classnames from 'classnames'

const useStyles = makeStyles<Theme, FeatureChipProps>(
  (theme) => ({
    beta: {
      color: 'white',
      backgroundColor: theme.paintscout.palette.secondary[200].C70,
      fontWeight: 'bold',
      borderRadius: theme.borderRadius.md,
      outline: `1px solid ${theme.paintscout.palette.secondary[200].C90}`
    },
    new: {
      color: 'white',
      backgroundColor: theme.paintscout.palette.brand.Blue.Blue65,
      fontWeight: 'bold',
      borderRadius: theme.borderRadius.md,
      outline: `1px solid ${theme.paintscout.palette.brand.Blue.Blue80}`
    },
    icon: {
      '& path': {
        fill: ({ variant }) =>
          variant === 'beta' ? theme.paintscout.palette.secondary[200].C70 : theme.paintscout.palette.brand.Blue.Blue65
      }
    },
    badgeRoot: {
      '& > div': {
        display: 'flex'
      }
    }
  }),
  { name: 'FeatureChip' }
)

const labels: Record<FeatureChipVariant, string> = {
  beta: 'Beta',
  new: 'New!'
}

export type FeatureChipVariant = 'beta' | 'new'

export interface FeatureChipProps extends Omit<ChipProps, 'classes' | 'variant'> {
  variant: FeatureChipVariant
  classes?: StyleClasses<typeof useStyles>
  collapseSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

function FeatureChip(props: FeatureChipProps) {
  const { variant, label, className, collapseSize, ...baseProps } = props
  const classes = useStyles(props)

  // If collapseSize is provided, the chip will collapse to an icon at the specified breakpoint and below
  // The logic looks weird to avoid calling useMediaQuery conditionally
  const isCollapsedSize = useMediaQuery(collapseSize ?? 'xl')
  const isCollapsed = collapseSize ? isCollapsedSize : false

  if (isCollapsed) {
    return (
      <Tooltip classes={{ badgeRoot: classes.badgeRoot }} hideIcon title={labels[variant]}>
        <NewAltIcon className={classnames(classes.icon, className)} fontSize="small" />
      </Tooltip>
    )
  }

  return (
    <Chip
      {...baseProps}
      className={classnames(classes[variant], className)}
      label={label ?? labels[variant]}
      size={baseProps.size ? baseProps.size : 'small'}
    />
  )
}

export default FeatureChip
